import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// Through the shared barrel, not straight from `lucide-react`. The direct import
// was a concurrency workaround from when `compose/Icon` was owned by another
// chunk; `Bell` is one of the 47 glyphs now sourced from `@norkron/web-icons`, and
// leaving a second icon source in place would mean the app quietly kept two.
import { Bell } from "../compose/Icon";
import type { NotificationItem } from "../api/types";
import { NotificationList } from "./NotificationList";
import { useNotifications } from "./useNotifications";
import "./notifications.css";

/**
 * The shell notification bell: an unread-count badge + a dropdown of the
 * caller's notifications (unread-first). Clicking a row deep-links to its
 * `source_ref` permalink (`/w/<slug>/doc/<docId>?c=<commentId>`) and marks it
 * read; "Mark all read" clears the badge. The dropdown dismisses on outside
 * click / Escape. All interactive elements carry `data-testid`.
 *
 * NB: the transient "a new notification just arrived" TOAST surface is a natural
 * fit for `@norkron/web-notifications` (Banner / BannerStack), but that library
 * carries the foundational web-* peer stack (web-a11y / web-themer /
 * web-error-boundary / web-conformance) that P2b-C0 wires. The bell itself is
 * shell chrome and is built from the app's own composed primitives until C0
 * lands; see MANIFEST for the drop-in Banner integration point.
 */
export function NotificationBell() {
  const { feed, markRead, markAll, refresh } = useNotifications();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unread = feed.unread_count;

  const onSelect = (n: NotificationItem) => {
    setOpen(false);
    if (!n.read_at) void markRead(n.id);
    navigate(n.source_ref); // permalink deep-link (?c= blooms the anchor)
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void refresh(); // freshen on open
  };

  return (
    <div className="notif" ref={rootRef}>
      <button
        type="button"
        className="notif__bell"
        data-testid="notification-bell"
        aria-label={
          unread > 0 ? `Notifications, ${unread} unread` : "Notifications"
        }
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <Bell size={18} strokeWidth={1.5} />
        {unread > 0 ? (
          <span className="notif__badge" data-testid="notification-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="notif__panel"
          role="menu"
          aria-label="Notifications"
          data-testid="notification-panel"
        >
          <div className="notif__panelhead">
            <span className="notif__paneltitle">Notifications</span>
            <button
              type="button"
              className="notif__markall"
              data-testid="notif-mark-all"
              onClick={() => void markAll()}
              disabled={unread === 0}
            >
              Mark all read
            </button>
          </div>
          <NotificationList items={feed.notifications} onSelect={onSelect} />
        </div>
      ) : null}
    </div>
  );
}
